import PropTypes from 'prop-types';
import { gql } from '@apollo/client';
import { get, keyBy, set, sortBy } from 'lodash-es';
import { EdmundsGraphQLFederation } from 'client/data/graphql/graphql-client';
import { GRAPHQL_FRAGMENTS } from 'client/data/constants/graphql-fragments';

import { createModelSegment } from 'client/data/luckdragon/segment';
import { parseContent } from 'client/data/cms/content';
import { withMetrics } from 'client/data/api/api-metrics';
import { EdmundsAPI } from 'client/data/api/api-client';
import { fetchContent } from 'client/data/cms/fetch-content';
import { dimensionsFeatureMap, specificationsFeatureMap } from 'client/data/models/car-features-map';
import { isNewState } from 'site-modules/shared/utils/publication-states';
import { formatStyleDetails } from 'site-modules/shared/utils/features-specs/utils';
import { PUB_STATES } from 'client/constants/pub-states';
import { LEAD_FORM_CONFIGS } from 'site-modules/shared/constants/lead-form/lead-form-configs';
import { CmsModel } from './cms';
import {
  buildNewTmvPricingPath,
  buildStylesPath,
  buildVehicleSubmodelStylePath,
  convertSubmodelStyleToStyle,
  FeatureSpecsPaths,
  VehicleModel,
} from './vehicle-v2';
import { buildReviewPath, EditorialReviewModel } from './editorial-review';
import { VisitorModel } from './visitor';
import { FeatureFlagModel } from './feature-flag';
import { ConsumerReviewsModel, ConsumerReviewsPaths } from './consumer-reviews';
import { getUnifiedLeadFormDealerCountV2Path, UnifiedLeadFormModel } from './unified-lead-form';

const NOT_AVAILABLE = 'not available';
const EMPTY_SUBMODEL_CLASS = 'empty';
const COMPARATOR_FEED_PATH = 'comparator/compare-venom';
const INVENTORY_FIELDS_TO_DISPLAY = [
  'type',
  'vin',
  'dealerInfo',
  'stockNumber',
  'historyInfo',
  'thirdPartyInfo',
  'prices',
  'computedInfo',
  'vehicleInfo.photo.defaultPhoto',
  'vehicleInfo.styleInfo',
  'vehicleInfo.partsInfo',
  'vehicleInfo.vehicleColors',
  'vehicleInfo.mileage',
].join(',');
export const NO_STYLE_ID = '0';

const SuggestedLinks = PropTypes.shape({
  id: PropTypes.string,
  header: PropTypes.string,
  links: PropTypes.arrayOf(
    PropTypes.shape({
      link: PropTypes.string,
      text: PropTypes.string,
      title: PropTypes.string,
    })
  ),
});
export const SuggestedLinksEntity = PropTypes.arrayOf(SuggestedLinks);

export function getBestComparatorVehiclesPath() {
  return `bestComparatorVehicles`;
}

function getMetadataValue(entry, key) {
  return entry && entry.hasMetadata && entry.hasMetadata(key) ? entry.metadata(key).value() : null;
}

function processLinks(links) {
  const linkSet = [];

  links.children().forEach(linkMeta => {
    linkSet.push({
      link: getMetadataValue(linkMeta, 'link'),
      text: getMetadataValue(linkMeta, 'text'),
      title: getMetadataValue(linkMeta, 'title'),
    });
  });

  return linkSet;
}

function transformSuggestedLinks(apiResponse) {
  const content = parseContent(apiResponse);
  const suggestedLinks = [];

  content.children().forEach(link => {
    suggestedLinks.push({
      id: link.id,
      header: getMetadataValue(link, 'header'),
      links: processLinks(link),
    });
  });

  return suggestedLinks;
}

/**
 * Gets all submodel classes for a list of submodels.
 * @param {Object} submodelMap  Mapping from submodels to submodelClasses
 * @param {Array} submodels     List of submodels in slug format
 */
function findSubmodelClasses(submodelMap, submodels) {
  return submodels.length > 0
    ? submodels
        .filter(submodel => submodel in submodelMap)
        .reduce((classes, submodel) => classes.add(submodelMap[submodel]), new Set())
    : new Set([EMPTY_SUBMODEL_CLASS]);
}

/**
 * Gets the name of the sponsorship based on class.
 * The keys of submodelClassMap keys are ordered by submodel class priority.
 * Only the first sponsorship match will be returned.
 * @param {Object} submodelClassMap Mapping from submodel class to sponsorship name
 * @param {Set} classes             Submodel classes
 */
function findSponsorshipName(submodelClassMap, classes) {
  const classKey = Object.keys(submodelClassMap).find(className => classes.has(className));
  return submodelClassMap[classKey];
}

/**
 * Gets the sponsorship data based on submodels.
 * Submodels are mapped to submodel classes, which are mapped to sponsorships.
 * @param {CmsEntities.Content} comparatorNativeSponsorship
 * @param {Array} submodels   List of submodels in slug format
 */
export function selectSponsorshipBySubmodels(comparatorNativeSponsorship, submodels) {
  const submodelMap = comparatorNativeSponsorship.metadata('submodelMap').json();
  const submodelClassMap = comparatorNativeSponsorship.metadata('submodelClassMap').json();
  const sponsorshipData = comparatorNativeSponsorship.metadata('sponsorshipData').json();
  const classes = findSubmodelClasses(submodelMap, submodels);
  const sponsorshipName = findSponsorshipName(submodelClassMap, classes);
  return sponsorshipName ? sponsorshipData[sponsorshipName] : null;
}

function isEmptyFeatureValue(features, path, value) {
  return get(features, `${path}`, NOT_AVAILABLE).toLowerCase().trim() === NOT_AVAILABLE && value !== 'N/A';
}

async function resolveComparableFeatures(context, styleId) {
  const [features, featureSpecs] = await Promise.all([
    context.resolveValue(`paramFeatures.${styleId}`, VehicleModel),
    context
      .resolveValue(FeatureSpecsPaths.buildStyleFeaturesPath(styleId), VehicleModel)
      .then(style => formatStyleDetails(style)),
  ]);
  if (get(featureSpecs, 'electric.isElectric')) {
    const name = specificationsFeatureMap.name;
    const values = keyBy(specificationsFeatureMap.values, 'camelName');

    if (isEmptyFeatureValue(features, `${name}.${values.enginePower.name}`, featureSpecs.horsepower)) {
      set(features, `${name}.${values.enginePower.name}`, featureSpecs.horsepower);
    }
    if (isEmptyFeatureValue(features, `${name}.${values.engineTorque.name}`, featureSpecs.torque)) {
      set(features, `${name}.${values.engineTorque.name}`, featureSpecs.torque);
    }
    if (isEmptyFeatureValue(features, `${name}.${values.engineCylinderCount.name}`, featureSpecs.engineType)) {
      set(features, `${name}.${values.engineCylinderCount.name}`, featureSpecs.engineType);
    }
  }
  if (get(featureSpecs, 'measurements.turningCircle')) {
    const name = dimensionsFeatureMap.name;
    const values = keyBy(dimensionsFeatureMap.values, 'camelName');
    set(features, `${name}.${values.turningCircle.name}`, featureSpecs.measurements.turningCircle);
  }
  if (get(featureSpecs, 'measurements.countryOfFinalAssembly')) {
    const name = specificationsFeatureMap.name;
    const values = keyBy(specificationsFeatureMap.values, 'camelName');
    set(features, `${name}.${values.countryOfFinalAssembly.name}`, featureSpecs.measurements.countryOfFinalAssembly);
  }
  return {
    ...features,
    params: {
      isTruck: !!featureSpecs?.truck?.isTruck,
      isElectric: !!featureSpecs?.electric?.isElectric,
      isPluginHybrid: !!featureSpecs?.electric?.isPluginElectric,
    },
  };
}

export const ComparatorModel = createModelSegment('comparator', [
  {
    path: 'sponsorship.empty',
    async resolve(match, context) {
      const cmsContent = await context.resolveValue(`content["${COMPARATOR_FEED_PATH}"]`, CmsModel).then(parseContent);
      if (!cmsContent.hasChild('comparator-native-sponsorship')) {
        return null;
      }
      const comparatorNativeSponsorship = cmsContent.child('comparator-native-sponsorship');
      return selectSponsorshipBySubmodels(comparatorNativeSponsorship, []);
    },
  },
  {
    path: 'sponsorship.byStyle["{styleId}"]',
    async resolve(match, context) {
      const { styleId } = match;
      const [cmsContent, { makeSlug, modelSlug }] = await Promise.all([
        context.resolveValue(`content["${COMPARATOR_FEED_PATH}"]`, CmsModel).then(parseContent),
        context.resolveValue(`styles.${styleId}`, VehicleModel),
      ]);
      if (!cmsContent.hasChild('comparator-native-sponsorship')) {
        return null;
      }
      const comparatorNativeSponsorship = cmsContent.child('comparator-native-sponsorship');
      const submodels = await context.resolveValue(
        `makes["${makeSlug}"].models["${modelSlug}"].submodels`,
        VehicleModel
      );
      return selectSponsorshipBySubmodels(comparatorNativeSponsorship, Object.keys(submodels));
    },
  },
  {
    path: 'styles["{styleIds}"].comparableFeatures["{category}"]',
    async resolve({ styleIds, category }, context) {
      return withMetrics(EdmundsAPI, context)
        .fetchJson(`/vehiclefeatures/v3/comparable-features/?styleid=${styleIds}&categories=${category}`)
        .then(response => Object.values(get(response, 'results', {})))
        .catch(() => []);
    },
  },
  {
    path: 'styles["{styleId}"].features["{category}"]["{feature}"].otherStyles',
    async resolve({ styleId, category, feature }, context) {
      const stylesList = await context
        .resolveValue(`styles.${styleId}`, VehicleModel)
        .then(style =>
          context.resolveValue(
            buildStylesPath({ make: style.makeSlug, model: style.modelSlug, year: style.year }),
            VehicleModel
          )
        )
        .then(styles => styles.filter(({ publicationState }) => publicationState !== PUB_STATES.PRE_PROD));

      return context
        .resolveValue(`styles["${stylesList.map(({ id }) => id).join(',')}"].comparableFeatures["${category}"]`)
        .then(stylesFeatures =>
          stylesFeatures
            .filter(styleFeatures => {
              const featureValue = get(styleFeatures, `[${category}][${feature}]`, '').toLowerCase();

              return featureValue && featureValue !== 'available on other styles' && featureValue !== 'not available';
            })
            .map(styleFeatures => get(styleFeatures, 'styleId'))
        )
        .then(otherStyleIds =>
          stylesList
            .filter(({ id }) => otherStyleIds.includes(id))
            .map(({ id, name, price, submodels }) => ({
              styleId: id,
              styleName: name,
              baseMSRP: get(price, 'baseMSRP'),
              submodelName: get(submodels, 'name'),
            }))
        )
        .catch(() => []);
    },
  },
  {
    path: 'comparableFeatures["{styleIds}"]',
    resolve({ styleIds }, context) {
      return Promise.all(
        styleIds
          .split(',')
          .map(styleId =>
            styleId !== NO_STYLE_ID ? resolveComparableFeatures(context, styleId) : Promise.resolve(null)
          )
      );
    },
  },
  {
    path: 'editorialReview["{styles}"]',
    resolve({ styles }, context) {
      return Promise.all(
        styles.split(',').map(value => {
          if (value !== NO_STYLE_ID) {
            const [styleId, submodel] = value.split('|');
            return context
              .resolveValue(buildVehicleSubmodelStylePath({ styleId, submodel }), VehicleModel)
              .then(data => {
                const style = convertSubmodelStyleToStyle(data);
                return (
                  style &&
                  context
                    .resolveValue(
                      buildReviewPath({
                        make: style.makeSlug,
                        model: style.modelSlug,
                        submodel: style.submodels?.slug,
                        year: style.year,
                      }),
                      EditorialReviewModel
                    )
                    .then(review => {
                      if (!review) return review;
                      // ratingsV4New uses a { ratingSections: [{ key, score }] } array shape.
                      // Normalize into the same flat { [key]: { rating } } shape as legacy ratings
                      // so downstream consumers (EditorialReviewRating) always get a consistent contract.
                      if (review.ratingsV4 && !review.ratings) {
                        const NORMALIZED_KEY_MAP = {
                          value: 'goodValue',
                          mpg: 'mpgOrRange',
                          tech: 'inCabinTech',
                          storage: 'storageCargoTowing',
                          'x-factor': 'xFactor',
                        };

                        const normalizedRatings = (review.ratingsV4.ratingSections || []).reduce(
                          (acc, { key, score }) => {
                            const normalizedKy = NORMALIZED_KEY_MAP[key] || key;
                            acc[normalizedKy] = { rating: score };
                            return acc;
                          },
                          {}
                        );
                        return { ...review, ratings: normalizedRatings };
                      }
                      return review;
                    })
                );
              });
          }
          return Promise.resolve(null);
        })
      );
    },
  },
  {
    path: 'consumerRating["{styleIds}"]',
    resolve({ styleIds }, context) {
      return Promise.all(
        styleIds.split(',').map(styleId =>
          styleId !== NO_STYLE_ID
            ? context.resolveValue(`styles.${styleId}`, VehicleModel).then(
                style =>
                  style &&
                  context.resolveValue(
                    ConsumerReviewsPaths.buildConsumerReviewsRatingsCountPath({
                      make: style.makeSlug,
                      model: style.modelSlug,
                      year: style.year,
                    }),
                    ConsumerReviewsModel
                  )
              )
            : Promise.resolve(null)
        )
      );
    },
  },
  {
    path: 'colors["{styleIds}"]',
    resolve({ styleIds }, context) {
      return Promise.all(
        styleIds
          .split(',')
          .map(styleId =>
            styleId !== NO_STYLE_ID
              ? context.resolveValue(`colorFeatures.${styleId}`, VehicleModel)
              : Promise.resolve(null)
          )
      );
    },
  },
  {
    path: 'tco["{styleIds}"]',
    resolve({ styleIds }, context) {
      return Promise.all(
        styleIds
          .split(',')
          .map(styleId =>
            styleId !== NO_STYLE_ID
              ? context
                  .resolveValue(`styles.${styleId}.fiveyearstco`, VehicleModel)
                  .then(result => get(result, 'total'))
              : Promise.resolve(null)
          )
      );
    },
  },
  {
    path: 'pricing["{styleIds}"]',
    resolve({ styleIds }, context) {
      return Promise.all(
        styleIds.split(',').map(styleId =>
          styleId !== NO_STYLE_ID
            ? context
                .resolveValue(`styles.${styleId}`, VehicleModel)
                .then(({ publicationState }) =>
                  isNewState(publicationState)
                    ? context.resolveValue(`${buildNewTmvPricingPath(styleId, false)}.totalWithOptions`, VehicleModel)
                    : Promise.resolve(null)
                )
                .catch(() => Promise.resolve(null))
            : Promise.resolve(null)
        )
      );
    },
  },
  {
    path: 'dealerCount["{styleIds}"]',
    async resolve({ styleIds }, context) {
      return Promise.all(
        styleIds.split(',').map(styleId =>
          styleId !== NO_STYLE_ID
            ? context
                .resolveValue(`styles.${styleId}`, VehicleModel)
                .then(({ publicationState, makeSlug, modelSlug }) =>
                  isNewState(publicationState)
                    ? context.resolveValue(
                        getUnifiedLeadFormDealerCountV2Path({
                          makeSlug,
                          modelSlug,
                          strategy: LEAD_FORM_CONFIGS.QUICK_QUOTES_TRIM_DST.strategy,
                        }),
                        UnifiedLeadFormModel
                      )
                    : Promise.resolve(null)
                )
                .catch(() => Promise.resolve(null))
            : Promise.resolve(null)
        )
      );
    },
  },
  {
    path: 'evEfficiency["{styleIds}"]',
    resolve({ styleIds }, context) {
      return Promise.all(
        styleIds.split(',').map(styleId =>
          styleId !== NO_STYLE_ID
            ? context.resolveValue(FeatureSpecsPaths.buildStyleFeaturesPath(styleId), VehicleModel).then(features => {
                if (!features) {
                  return null;
                }

                const styleDetails = formatStyleDetails(features).electric;

                return {
                  ...styleDetails,
                  mpge: `${styleDetails.mpge.city} / ${styleDetails.mpge.hwy} / ${styleDetails.mpge.combined} MPGe`,
                };
              })
            : Promise.resolve(null)
        )
      );
    },
  },
  {
    path: 'evTaxCredit["{styleIds}"]',
    async resolve({ styleIds }, context) {
      return context.resolveValue(`evEfficiency["${styleIds}"]`).then(async styleFeatures => {
        if (!(
          styleFeatures &&
          styleFeatures.some(
            evComparableFeature =>
              get(evComparableFeature, 'isElectric') || get(evComparableFeature, 'isPluginElectric')
          )
        )) {
          return Promise.resolve([]);
        }

        const zipCode = await context.resolveValue('location.zipCode', VisitorModel);
        const styleIdsArray = styleIds.split(',');

        return withMetrics(EdmundsGraphQLFederation, context)
          .query(
            gql`
              query (
                $styleId0: String!
                $styleId1: String!
                $styleId2: String!
                $styleId3: String!
                $zipCode: String!
              ) {
                incentives0: incentives(
                  incentiveFilters: { zip: $zipCode, styleIds: [$styleId0], subtypeIds: { in: [517, 640] } }
                ) {
                  incentives {
                    rebateAmount
                  }
                }
                incentives1: incentives(
                  incentiveFilters: { zip: $zipCode, styleIds: [$styleId1], subtypeIds: { in: [517, 640] } }
                ) {
                  incentives {
                    rebateAmount
                  }
                }
                incentives2: incentives(
                  incentiveFilters: { zip: $zipCode, styleIds: [$styleId2], subtypeIds: { in: [517, 640] } }
                ) {
                  incentives {
                    rebateAmount
                  }
                }
                incentives3: incentives(
                  incentiveFilters: { zip: $zipCode, styleIds: [$styleId3], subtypeIds: { in: [517, 640] } }
                ) {
                  incentives {
                    rebateAmount
                  }
                }
              }
            `,
            {
              zipCode,
              styleId0: styleIdsArray[0],
              styleId1: styleIdsArray[1],
              styleId2: styleIdsArray[2],
              styleId3: styleIdsArray[3],
            }
          )
          .then(styleIncentives =>
            [
              styleIncentives.incentives0,
              styleIncentives.incentives1,
              styleIncentives.incentives2,
              styleIncentives.incentives3,
            ].map(incentives => incentives.incentives.reduce((total, { rebateAmount }) => total + rebateAmount, 0))
          );
      });
    },
  },
  {
    path: 'vins["{vins}"].inventories',
    async resolve({ vins }, context) {
      const { zipCode, dma, latitude, longitude } = await context.resolveValue('location', VisitorModel);
      const { results } = await withMetrics(EdmundsAPI, context).fetchJson(
        `/inventory/v5/find/?vin=${vins}&pagesize=4&pagenum=1&radius=5000&zip=${zipCode}&lat=${latitude}&lon=${longitude}&userDma=${dma}&fields=${INVENTORY_FIELDS_TO_DISPLAY}`
      );
      const arrayOfVins = vins.split(',');
      return sortBy(results, item => arrayOfVins.indexOf(item.vin));
    },
  },
  {
    path: 'vins["{vins}"].optionPrices',
    async resolve({ vins }, context) {
      const dataMap = {};
      const inventories = await context.resolveValue(`vins["${vins}"].inventories`);
      const optionPricesPromises = inventories.map(vinData => {
        const styleId = get(vinData, 'vehicleInfo.styleInfo.styleId', '');
        const nonColorOptionIds = get(vinData, 'vehicleInfo.partsInfo.nonColorOptionIds', []);
        if (!styleId || !nonColorOptionIds.length) {
          return {};
        }
        const url = `/vehicle/v3/calculateoptionprice?style=${styleId}&options=${nonColorOptionIds.join(',')}`;
        return withMetrics(EdmundsAPI, context).fetchJson(url);
      });
      const optionPrices = await Promise.all(optionPricesPromises);
      vins.split(',').forEach((vin, index) => {
        dataMap[vin] = optionPrices[index];
      });

      return dataMap;
    },
  },
  {
    path: 'suggestedLinks',
    resolve(match, context) {
      const path = `research/compare-data/popular-data-sets`;

      return fetchContent(path, context)
        .then(apiResponse => transformSuggestedLinks(apiResponse))
        .catch(() => null);
    },
  },
  /**
   * @see getBestComparatorVehiclesPath
   */
  {
    path: 'bestComparatorVehicles',
    async resolve(match, context) {
      const enabled = context.getValue('showGraphqlComparatorLinks', FeatureFlagModel);
      if (!enabled) {
        return context.abort();
      }
      const graphqlFailSafeDisabled = context.getValue('disableGraphqlComparatorLinksFailSafe', FeatureFlagModel);
      const [part1, part2] = await Promise.all([
        withMetrics(EdmundsGraphQLFederation, context).query(gql`
          ${GRAPHQL_FRAGMENTS.comparatorSegmentFields}
          query {
            suv: editorialVehicleRankings(edmundsTypeCategory: "SUV") {
              ...comparatorSegmentFields
            }
            truck: editorialVehicleRankings(edmundsTypeCategory: "Truck") {
              ...comparatorSegmentFields
            }
            electric: editorialVehicleRankings(edmundsTypeCategory: "Electric") {
              ...comparatorSegmentFields
            }
            hybrid: editorialVehicleRankings(edmundsTypeCategory: "Hybrid") {
              ...comparatorSegmentFields
            }
            sedan: editorialVehicleRankings(edmundsTypeCategory: "Sedan") {
              ...comparatorSegmentFields
            }
          }
        `),
        withMetrics(EdmundsGraphQLFederation, context).query(gql`
          ${GRAPHQL_FRAGMENTS.comparatorSegmentFields}
          query {
            hatchback: editorialVehicleRankings(edmundsTypeCategory: "Hatchback") {
              ...comparatorSegmentFields
            }
            coupe: editorialVehicleRankings(edmundsTypeCategory: "Coupe") {
              ...comparatorSegmentFields
            }
            convertible: editorialVehicleRankings(edmundsTypeCategory: "Convertible") {
              ...comparatorSegmentFields
            }
            minivan: editorialVehicleRankings(edmundsTypeCategory: "Minivan") {
              ...comparatorSegmentFields
            }
            wagon: editorialVehicleRankings(edmundsTypeCategory: "Wagon") {
              ...comparatorSegmentFields
            }
          }
        `),
      ]);
      return Object.values({ ...part1, ...part2 })
        .map(category => {
          const header = category[0].edmundsTypeCategory;
          const links = category.map(segment => {
            const vehicles = segment.segmentRatings
              .filter(
                ({ baseStyleId, modelYear }) =>
                  modelYear?.makeSlug &&
                  modelYear?.modelSlug &&
                  modelYear?.makeName &&
                  modelYear?.modelName &&
                  (graphqlFailSafeDisabled || baseStyleId)
              )
              .slice(0, 4);
            const isValidComparison = vehicles.length >= 2;

            return {
              title: `Compare ${segment.displayName}`,
              link: isValidComparison
                ? `/car-comparisons/${vehicles
                    .map(({ modelYear }) => `${modelYear.makeSlug}-${modelYear.modelSlug}`)
                    .join('-vs-')}/`
                : null,
              text: isValidComparison
                ? vehicles.map(({ modelYear }) => `${modelYear.makeName} ${modelYear.modelName}`).join(' vs. ')
                : '',
              compare: isValidComparison ? vehicles.length : 0,
            };
          });
          return { header, links: links.filter(({ compare }) => compare > 0) };
        })
        .filter(({ links }) => links.length);
    },
  },
]);
